import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddressAutocomplete from '../../components/pro/AddressAutocomplete';
import ReleveDeposeur from '../../components/pro/ReleveDeposeur';
import { Page, EnTetePage, Etiquette } from '../../components/pro/ui';

// Nouveau devis — la seule porte d'entrée d'un devis.
//
// On dit pour qui on chiffre, on verse ce qu'on a rapporté de la visite
// (dictées, photos, plans, cotes), et l'analyse crée le devis : elle ouvre le
// lead, y range le dossier, propose pièces et postes. On arrive ensuite sur la
// page du devis, à l'écran des constats, pour corriger et poursuivre. Le
// wizard manuel d'avant (/pro/devis/new) n'existe plus : on relève d'abord.

const ETATS = [
  { id: 'neuf', label: 'Neuf / récent' },
  { id: 'bon_etat', label: 'Bon état' },
  { id: 'a_renover', label: 'À rénover' },
  { id: 'mauvais', label: 'Vétuste' },
];

const TYPES_BIEN = [
  { id: 'maison', label: 'Maison' },
  { id: 'appartement', label: 'Appartement' },
  { id: 'local', label: 'Local commercial' },
  { id: 'bureau', label: 'Bureau' },
];

// La TVA était figée à 10 %. C'est le taux courant d'une rénovation de logement
// de plus de deux ans, mais pas celui d'un local commercial (20 %) ni d'une
// amélioration énergétique (5,5 %) : le choix revient à celui qui a vu le
// chantier, pas au code.
const TVA = [
  { id: 0.10, label: 'TVA 10 % — logement de plus de 2 ans' },
  { id: 0.055, label: 'TVA 5,5 % — amélioration énergétique' },
  { id: 0.20, label: 'TVA 20 % — taux normal' },
];

const CHANTIER_VIDE = {
  prenom: '', nom: '', telephone: '', email: '',
  adresse: '', ville: '', typeBien: 'maison', etat: '', tva: 0.10,
};

export default function ProDeviseur() {
  const navigate = useNavigate();
  const [chantier, setChantier] = useState(CHANTIER_VIDE);
  const majChantier = (champ, valeur) => setChantier(c => ({ ...c, [champ]: valeur }));
  const sansNom = !chantier.nom.trim() && !chantier.prenom.trim();

  // Le devis existe dès que l'analyse a rendu : on y va, à l'écran des
  // constats — la fiche client est déjà remplie, les ressources déposées.
  const ouvrirLeDevis = (resultat) => {
    if (!resultat?.ref) return;
    try { sessionStorage.setItem(`pro_step:${resultat.ref}`, '3'); } catch { /* sans mémoire d'étape, on arrive au client */ }
    navigate(`/pro/devis/${resultat.ref}`);
  };

  return (
    <Page large>
      <EnTetePage
        titre="Nouveau devis"
        sous="Le client, puis ce que vous avez rapporté de la visite. L'analyse crée le devis et vous y conduit." />

      {/* La page suit l'ordre du travail : on dit pour qui on chiffre, on
          verse ce qu'on a rapporté de la visite, puis on lance. Chaque étape
          est une carte, et son numéro n'est pas un ornement — sans la
          première, la deuxième ne sait pas quel taux de TVA appliquer. */}

      <Section numero="1" titre="Le client" sous="Pour qui, où, et sur quel bien.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block">
            <Etiquette className="mb-1">Prénom</Etiquette>
            <input value={chantier.prenom} onChange={e => majChantier('prenom', e.target.value)}
                   placeholder="Camille" className="champ w-full" />
          </label>
          <label className="block">
            <Etiquette className="mb-1">Nom</Etiquette>
            <input value={chantier.nom} onChange={e => majChantier('nom', e.target.value)}
                   placeholder="Barraud" className="champ w-full" />
          </label>
          <label className="block">
            <Etiquette className="mb-1">Téléphone</Etiquette>
            <input type="tel" value={chantier.telephone} onChange={e => majChantier('telephone', e.target.value)}
                   placeholder="06 12 34 56 78" className="champ w-full" />
          </label>
          <label className="block">
            <Etiquette className="mb-1">E-mail</Etiquette>
            <input type="email" value={chantier.email} onChange={e => majChantier('email', e.target.value)}
                   placeholder="camille@exemple.fr" className="champ w-full" />
          </label>
          <div className="sm:col-span-2">
            <Etiquette className="mb-1">Adresse du chantier</Etiquette>
            <AddressAutocomplete value={chantier.adresse}
                                 onChange={(d) => setChantier(c => ({ ...c, ...d }))} />
          </div>
          <label className="block">
            <Etiquette className="mb-1">Type de bien</Etiquette>
            <select value={chantier.typeBien} onChange={e => majChantier('typeBien', e.target.value)} className="champ w-full">
              {TYPES_BIEN.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          <label className="block">
            <Etiquette className="mb-1">État du bien</Etiquette>
            <select value={chantier.etat} onChange={e => majChantier('etat', e.target.value)} className="champ w-full">
              {/* Laissé sur « d'après la dictée », l'état se déduit de l'audio.
                  Renseigné, il prime : celui qui a vu le chantier sait mieux. */}
              <option value="">D'après la dictée</option>
              {ETATS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
            </select>
          </label>
          <label className="block sm:col-span-2 lg:col-span-4">
            <Etiquette className="mb-1">TVA applicable</Etiquette>
            <select value={chantier.tva} onChange={e => majChantier('tva', parseFloat(e.target.value))} className="champ w-full">
              {TVA.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
        </div>
      </Section>

      <Section numero="2" titre="Le relevé" sous="La dictée, les photos et plans, les cotes — dans l'ordre que vous voulez.">
        <ReleveDeposeur chantier={chantier} onTermine={ouvrirLeDevis}
                        bloque={sansNom} raisonBlocage="Indiquez au moins le nom ou le prénom du client : le devis sera à son nom." />
      </Section>
    </Page>
  );
}

// Une étape de l'écran : son numéro, son titre, ce qu'elle attend.
function Section({ numero, titre, sous, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-4">
      <div className="flex items-baseline gap-3 mb-4">
        <span className="shrink-0 w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold flex items-center justify-center self-center">{numero}</span>
        <div className="min-w-0">
          <h2 className="font-semibold text-slate-900 text-sm">{titre}</h2>
          {sous && <p className="text-xs text-slate-400 mt-0.5">{sous}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}
